import React, { useContext, useState, memo } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { sortByDate } from "@utils/sorters";
import ObjectPreview from "./ObjectPreview";
import { formatDate, isValidDate } from "@utils/dates";
import { dueDateClassForTask } from "@utils/dueDateClassForTask";
import { groupBy } from "@utils/groupBy";
import { useLocalStorage } from "@uidotdev/usehooks";
import { Grid, ToggleButtonGroup } from "@mui/joy";
import { FileThumbnail } from "@components/FileThumbnail";
import { useFormContext } from "react-hook-form";
import Uploady, {
  useUploady, useBatchAddListener,
  useItemFinalizeListener,
  useItemProgressListener,
  useAbortItem,
  FILE_STATES,
  useItemFinishListener,
  useItemErrorListener,
  useItemAbortListener
} from "@rpldy/uploady";
import UploadButton from "@rpldy/upload-button";
import UploadDropZone from "@rpldy/upload-drop-zone";
import { AddRowButton } from "./AddRowButton";
import { AddFileThumbnail } from "@components/AddFileThumbnail";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";

interface ColumnWithValue {
  label: string;
  value: string;//|number|null;
  transformer?: never;
}
interface ColumnWithTransformer {
  label: string;
  value?: never;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const ObjectRelationshipFilesEdit = memo(function ObjectRelationshipFilesEdit({
  relationship = [],
  title,
  tint = null,
  // columns,
  collapsible = false,
  note = null,
}) {
  const { control, register, setValue, getValues, watch } = useFormContext();
  const data = watch(relationship) ?? [];

  const location = useLocation();
  const { hash, pathname, search } = location;
  const isCreate = pathname.includes('/create');

  const uploady = useUploady();

  const [isExpanded, setIsExpanded] = useState(false);
  const [fileLayout, setFileLayout] = useLocalStorage('file_layout', 'list');

  const perPage = 5;

  const canCollapse = collapsible && data.length > perPage;
  const isEmpty = data.length === 0;

  const fileDidChange = (file, deleted = false) => {
    let fileIndex = data.findIndex(d => d.id === file.id);

    const _data = [...data];
    if(deleted) {
      _data.splice(fileIndex, 1);
    } else {
      _data[fileIndex] = file;
    }

    setValue(relationship, _data);
  };

  const addRow = () => {
    uploady.showFileUpload();
  }

  const [pendingUploadCount, setPendingUploadCount] = useState(0);

  const decrementPending = () => {
    setPendingUploadCount((count) => Math.max(0, count - 1));
  };

  useBatchAddListener((batch) => {
    setPendingUploadCount((count) => count + 1);
  });

  // uploading happens in ObjectEdit but this is the only place we can safely check & update
  useItemFinishListener((item, options) => {
    if(
      item.uploadStatus >= 200
      && item.uploadStatus < 400
      && Array.isArray(item.uploadResponse?.data)
    ) {
      setValue(relationship, item.uploadResponse.data);

      setPendingUploadCount((count) => {
        const newCount = Math.max(0, count - 1);
        if(newCount <= 0) {
          toast.success('Files uploaded!', {
            id: 'files-uploading',
          });
        }
        return newCount;
      });

      return;
    }

    // a 2xx we couldn't parse is still a failure as far as the user is concerned
    decrementPending();
    toast.error(`${item.file.name} did not save. Please try again.`, {
      id: `file-error-${item.id}`,
    });
  });

  useItemErrorListener((item) => {
    decrementPending();

    if(item.uploadStatus === 401) {
      toast.error('Your session expired. Please log in again and re-upload.', {
        id: 'files-uploading',
      });
      return;
    }

    toast.error(`${item.file.name} failed to upload. Please try again.`, {
      id: `file-error-${item.id}`,
    });
  });

  useItemAbortListener(() => {
    decrementPending();
  });

  // TODO: allow switching between grid/list view
  return (
    <UploadDropZone
      onDragOverClassName="drag-over"
      grouped
      maxGroupSize={1}
    >
    <ObjectRelationshipCard
      unpadded
      // empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
            /*
            '.drag-over &': {
              bgcolor: theme.palette.success[200],
            },
            */
          })}
        >
          <CardActions
            sx={theme => ({
              py: 1.5,
              // pointerEvents: canCollapse ? 'all' : 'none',
              cursor: 'pointer',
              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={0.5}
              sx={{flex: 1}}
            >
              <Typography sx={theme => ({flex: 1, fontWeight: '600', color: 'white' })}>
                {title}
                {pendingUploadCount > 0 && <>{` (${pendingUploadCount} pending)`}</>}
                </Typography>
              {/* {!isEmpty && ( */}
              <ToggleButtonGroup
                value={fileLayout}
                size="sm"
                color="neutral"
                // borderRadius
                // variant="outline"
                onChange={(e:MouseEvent, val:string) => setFileLayout(val)}
                sx={theme => ({
                  '--variant-outlinedHoverBg': theme.palette.background.surface,
                  '--variant-outlinedActiveBg': theme.palette.background.surface,
                  '--Icon-color': theme.palette.background.surface,
                  '--IconButton-size': '1.65rem',
                  borderRadius: '4px',
                  '[aria-pressed="true"] svg, .MuiIconButton-root:hover svg': {
                    color: theme.palette.neutral.outlinedColor,
                  },
                  'svg': {
                    width: '1.05rem',
                    height: '1.05rem',
                    color: theme.palette.background.level3,
                  },
                  pr: 1,
                })}
              >
                <Tooltip title="List View">
                  <IconButton value="list">
                    <ViewListRoundedIcon/>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Grid View">
                  <IconButton value="grid">
                    <GridViewRoundedIcon/>
                  </IconButton>
                </Tooltip>
              </ToggleButtonGroup>
              {/* )} */}
              <AddRowButton onClick={addRow}/>
            </Stack>
          </CardActions>
        </CardOverflow>
      }
      sx={(theme) => ({
        '.drag-over &': {
          // opacity: 0.8,
          boxShadow: theme.shadow.xl,
          '--joy-shadowChannel': theme.vars.palette.success.mainChannel,
          '--joy-shadowOpacity': 0.2,
          // '--joy-shadowRing': 'inset 0 -3px 0 rgba(0 0 0 / 0.24)',
        }
      })}
    >
      {note !== null && <>{note}</>}
      {/* <CardOverflow> */}
      {fileLayout === 'list' && (
        <Table
          stickyHeader
          stripe="odd"
          // hoverRow
          sx={{
            margin: 0,
            padding: 0,
            tableLayout: 'auto',
          }}
        >
          <thead>
            <tr>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <AddFileThumbnail onClick={addRow} layout={fileLayout} />
              </td>
            </tr>
            {data.map((file, fileIndex:number) => (
              <tr key={fileIndex}>
                <td>
                  <FileThumbnail
                    file={file}
                    layout={fileLayout}
                    editable
                    fileDidChange={fileDidChange}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {fileLayout === 'grid' && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            p: 2,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              // xl: 'repeat(4, 1fr)',
            }
          }}
        >
          <AddFileThumbnail onClick={addRow} layout={fileLayout} />
          {/* <Box key={fileIndex}> */}
          {data.map((file, fileIndex:number) => (
            <FileThumbnail
              key={fileIndex}
              file={file}
              layout={fileLayout}
              editable
              fileDidChange={fileDidChange}
            />
          ))}
          {/* </Box> */}
        </Box>
      )}

      {/* </CardOverflow> */}

    {isCreate === true && <Typography sx={{
      textAlign: 'center',
      fontSize: 'sm',
      p: 1,
    }}>Note: files will upload after you save</Typography>}
    </ObjectRelationshipCard>
    </UploadDropZone>
  );
});

export default ObjectRelationshipFilesEdit;